import { Controller } from 'stimulus'
import { Dropzone } from 'dropzone'

import { getCsrfParams } from '~/common/helpers/csrf'
import { createAWSUploadHandler } from '../../javascript/admin/components/asset_uploader/helpers'
import { findPreviewListIndex } from './preview_list_index'

import './dropzone_component.scss'

export default class extends Controller {
  static targets = [
    'dropzoneEl',
    'dropzonePreviewContainer',
    'dropzoneUploaderContainer',
    'previewTemplate',
    'input',
    'dropzoneErrorContainer',
  ]

  static values = {
    style: String,
    maxFileSize: Number,
    signerUrl: String,
    awsAccessKeyId: String,
    bucket: String,
    path: String,
    objectName: String,
  }

  // Tracks the next selection index to assign to files
  selectionCounter = 0
  // Maps selection index to asset ID once upload completes
  completedUploads = new Map()

  connect() {
    let [param, value] = getCsrfParams()
    const csrf = {}
    csrf[param] = value
    const self = this

    const options = {
      url: '#',
      parallelUploads: 10,
      maxFiles: 100,
      params() {
        return csrf
      },
      disablePreviews: self.hoverStyle(),
      maxFilesize: self.maxFileSizeValue || 100,
      createImageThumbnails: false,
      autoProcessQueue: false,
      acceptedFiles: [
        'image/*',
        'video/*',
        'audio/*',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/rtf',
        'text/plain',
        'text/calendar',
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.csv',
        '.xls',
        '.xlsx',
      ].join(', '),
      accept: function (file) {
        file.selectionIndex = self.selectionCounter++
        self.addEvaporate(file)
        this.removeFile(file)
      },
    }
    this.dropzone = new Dropzone(self.dropzoneElTarget, options)
    if (self.hoverStyle()) {
      self.showOnDragEvent()
      self.hideOnFileAddOrDragLeave()
      self.showError()
    }

    if (self.formEl()) {
      self.formEl().addEventListener('reset', self.formResetHandler)
    }

    // Seed completedUploads with pre-existing IDs (edit mode) using negative
    // indices so they sort before newly-uploaded files.
    const existingIds = self.inputTarget.value.split(',').filter(Boolean)
    existingIds.forEach((id, i) => {
      self.completedUploads.set(i - existingIds.length, id)
    })
  }

  disconnect() {
    if (this.formEl()) {
      this.formEl().removeEventListener('reset', this.formResetHandler)
    }
    this.dropzone.destroy()
  }

  formResetHandler = () => {
    this.dropzonePreviewContainerTarget.dispatchEvent(
      new Event('splide:removeAllSlides')
    )
    this.inputTarget.value = ''
    this.selectionCounter = 0
    this.completedUploads.clear()
    this.dispatch('assets-changed', { prefix: false, bubbles: true })
  }

  showOnDragEvent() {
    this.bodyElement().addEventListener('dragenter', (event) => {
      const types = event?.dataTransfer?.types || []
      if (types.includes('Files')) {
        this.showDropzoneUploaderContainer()
      }
    })
    this.bodyElement().addEventListener('dragover', (event) => {
      const types = event.dataTransfer.types
      if (types.includes('Files')) {
        this.showDropzoneUploaderContainer()
        this.openNewContributionModal()
      }
    })
  }

  hideOnFileAddOrDragLeave() {
    this.dropzone.on('addedfile', (file) => {
      if (this.anyOtherActiveDropzonePresent()) {
        return
      }

      const clone = this.previewTemplateTarget.content.cloneNode(true)
      clone.querySelector('.js-preview-loader').dataset.uuid = file.upload.uuid
      this.hideDropzoneUploaderContainer()
      this.addItem(clone.firstElementChild.outerHTML, true)
      this.dropzonePreviewContainerTarget.classList.remove('hidden')
      this.toggleFormSubmitBtn(true)
    })

    this.bodyElement().addEventListener('dragleave', (e) => {
      if (!this.insideFormContainer(e)) {
        this.hideDropzoneUploaderContainer()
      }
    })

    this.bodyElement().addEventListener('dragend', (e) => {
      if (!this.insideFormContainer(e)) {
        this.hideDropzoneUploaderContainer()
      }
    })

    this.bodyElement().addEventListener('drop', () => {
      this.hideDropzoneUploaderContainer()
    })
  }

  onSuccess(response, file) {
    const selector = `[data-uuid='${file.upload.uuid}']`
    const domIndex = this.getIndexOfLi(selector)
    if (domIndex !== null) {
      this.removeItem(domIndex)
    } else {
      // getIndexOfLi couldn't resolve a Splide index for this placeholder — fall
      // back to removing it directly so a stuck loader can't leave uploads
      // (and therefore Send) permanently blocked. Splide appends when index is
      // null, so the completed thumbnail still shows up.
      this.dropzonePreviewContainerTarget
        .querySelector(selector)
        ?.closest('li')
        ?.remove()
    }
    this.addItem(
      response.thumbnail_preview,
      response.preview,
      domIndex,
      response.id,
      response.pusher_channel
    )
    // Use selection order index (captured at addedfile time) for input ordering.
    // Always register the upload, even when the preview swap above couldn't
    // locate the placeholder — the message must stay sendable either way.
    const newId = response.id.toString()
    this.completedUploads.set(file.selectionIndex, newId)
    this.rebuildInputValue(newId)
  }

  getIndexOfLi(selector) {
    const ulEl = this.dropzonePreviewContainerTarget.querySelector('ul')
    return findPreviewListIndex(ulEl, selector)
  }

  onError(errorMessage, file) {
    this.removeItem(`li:has([data-uuid='${file.upload.uuid}'])`)
    const span = document.createElement('span')
    span.innerText = `${file.name}: ${errorMessage}`
    this.dropzoneErrorContainerTarget.append(span)
    this.dropzoneErrorContainerTarget.classList.remove('hidden')
    setTimeout(function () {
      span.style.transition = 'opacity .5s ease-out'
      span.style.opacity = 0
      setTimeout(function () {
        span.remove()
      }, 1000)
    }, 10000)
    this.dispatch('assets-changed', { prefix: false, bubbles: true })
  }

  showError() {
    const self = this
    this.dropzone.on('error', (file, message) => {
      if (message === 'Upload canceled.') {
        return
      }

      self.onError(message, file)
    })
  }

  bodyElement() {
    return document.body
  }

  rebuildInputValue(newlyAddedId = null) {
    // Evict any IDs that were deleted via the preview UI (which updates
    // inputTarget.value directly). Skip newlyAddedId because it hasn't been
    // written to inputTarget.value yet — it's the ID that triggered this call.
    const currentIds = new Set(this.inputTarget.value.split(',').filter(Boolean))
    for (const [index, id] of this.completedUploads.entries()) {
      if (id !== newlyAddedId && !currentIds.has(id)) {
        this.completedUploads.delete(index)
      }
    }

    // Sort by selection index to preserve original file selection order
    const sortedIds = Array.from(this.completedUploads.entries())
      .sort((a, b) => a[0] - b[0])
      .map((entry) => entry[1])
    this.inputTarget.value = sortedIds.join(',')
    this.dispatch('assets-changed', { prefix: false, bubbles: true })
  }

  hideDropzoneUploaderContainer() {
    if (this.anyOtherActiveDropzonePresent()) {
      return
    }

    this.dropzoneUploaderContainerTarget.classList.add('hidden')
  }

  showDropzoneUploaderContainer() {
    if (this.anyOtherActiveDropzonePresent()) {
      return
    }

    this.dropzoneUploaderContainerTarget.classList.remove('hidden')
    this.resizeUploaderContainer()
  }

  hoverStyle() {
    return this.styleValue === 'hover'
  }

  formContainerSelector() {
    return '.js-dropzone-form'
  }

  formContainerElement() {
    return this.element.closest(this.formContainerSelector())
  }

  formEl() {
    return this.element.closest('form')
  }

  insideFormContainer(event) {
    if (this.formContainerElement()) {
      return (
        this.formContainerElement() ===
        event.target.closest(this.formContainerSelector())
      )
    } else {
      return false
    }
  }

  newContributionFormContainerSelector() {
    return "[data-community--new-contribution-target='formContainer']"
  }

  // When a file is dragged to the page open the new contribution modal if it is not open already
  // Also check if any other modal are open
  openNewContributionModal() {
    if (
      this.anyOtherActiveDropzonePresent() ||
      !this.element.closest(this.newContributionFormContainerSelector())
    ) {
      return
    }

    let anyModalVisible = false

    document
      .querySelectorAll(
        '[data-controller="modal"] [data-modal-target="contentContainer"]'
      )
      .forEach((modal) => {
        if (!anyModalVisible) {
          anyModalVisible = !modal.classList.contains('hidden')
        }
      })

    let newContributionModalVisible = false

    document
      .querySelectorAll(this.newContributionFormContainerSelector())
      .forEach((container) => {
        if (!newContributionModalVisible) {
          newContributionModalVisible = !container.classList.contains('hidden')
        }
      })

    if (!anyModalVisible && !newContributionModalVisible) {
      document.getElementById('js-new-contribution-btn').click()
      this.resizeUploaderContainer()
    }
  }

  addEvaporate(file) {
    const self = this
    const uploadHandler = createAWSUploadHandler({
      signerUrl: this.signerUrlValue,
      awsAccessKeyId: this.awsAccessKeyIdValue,
      bucket: this.bucketValue,
      path: this.pathValue,
      objectName: this.objectNameValue,
      onProgress: ({ percentage, remaining, file }) => {
        const progressBar = self.getRelevantElement(
          file,
          '.js-upload-progress-bar'
        )

        // We show 4% as the minimum progress bar width
        if (percentage > 4 && progressBar) {
          progressBar.style.width = percentage + '%'
        }

        const timeRemContainer = self.getRelevantElement(
          file,
          '.js-time-remaining'
        )
        if (timeRemContainer) timeRemContainer.innerHTML = remaining
      },
      onComplete: (response, file) => {
        self.onSuccess(response, file)
        self.toggleFormSubmitBtn(false)
      },
      onError: (errorMessage, file) => {
        self.onError(errorMessage, file)
      },
      onCancel: (file) => {
        const selector = `[data-uuid='${file.upload.uuid}']`
        const index = self.getIndexOfLi(selector)
        if (index !== null) self.removeItem(index)
      },
    })

    uploadHandler
      .uploadFile(file)
      .then(({ cancel }) => {
        const cancelBtn = self.getRelevantElement(file, '.js-cancel')
        cancelBtn?.addEventListener('click', function (e) {
          cancel().then(function () {
            const selector = `[data-uuid='${file.upload.uuid}']`
            const index = self.getIndexOfLi(selector)
            if (index !== null) self.removeItem(index)
          })
          e.preventDefault()
        })
      })
      .catch((error) => {
        console.error('Upload failed:', error)
      })
  }

  addItem(thumbnail, preview, index, assetId, pusherChannel) {
    const event = new CustomEvent('splide:addItem', {
      detail: {
        thumbnail: thumbnail,
        preview: preview,
        index: index,
        objectId: assetId,
        pusherChannel: pusherChannel,
      },
    })
    this.dropzonePreviewContainerTarget.dispatchEvent(event)
  }

  removeItem(index) {
    this.dispatch('removeItem', {
      detail: {
        index: index,
      },
    })
  }

  resizeUploaderContainer() {
    this.dropzoneUploaderContainerTarget.style.width = `${
      this.element.offsetWidth + 32
    }px`
  }

  toggleFormSubmitBtnEvent = (event) => {
    this.toggleFormSubmitBtn(event.detail.disable)
  }

  toggleFormSubmitBtn(disable = true) {
    // Check if there are any preview loader elements present on the preview component before enabling the form submit button
    // If there are any it means that there are still some assets being uploaded
    if (
      !disable &&
      (this.dropzonePreviewContainerTarget.querySelectorAll(
        '.js-preview-loader'
      ).length !== 0 ||
        this.dropzonePreviewContainerTarget.querySelectorAll(
          '.js-thumbnail.js-deleting-attachment'
        ).length !== 0)
    ) {
      return
    }

    if (this.formEl()) {
      const btn = this.formEl().querySelector("button[type='submit']")
      if (btn) {
        btn.disabled = disable
      }
    }
  }

  anyOtherActiveDropzonePresent() {
    if (this.element.classList.contains('active')) {
      return false
    } else if (
      document.querySelectorAll('.js-dropzone-container.active').length > 0
    ) {
      return true
    } else {
      return false
    }
  }

  addFiles(event) {
    const files = event.detail.files
    if (files.length > 0) {
      for (var i = 0; i < files.length; i++) {
        this.dropzone.addFile(files[i])
      }
    }
  }

  getRelevantElement(file, selector) {
    return this.dropzonePreviewContainerTarget.querySelector(
      `[data-uuid='${file.upload.uuid}'] ${selector}`
    )
  }
}
